import {useLocale} from "../context/LocaleContext";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { HeartHandshake, Loader2, LogIn, Minus, Plus } from "lucide-react";
import { formErrorMessage } from "../formFeedback";

const DUA_TYPES = [
  "Hatim",
  "Salâtü Selâm",
  "Kelime-i Tevhid",
  "Sure (Yâsîn-i Şerîf)",
  "Tesbihat",
];

export default function DuaPage() {
 const {t: translate, locale: currentLocale}=useLocale();

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [duaType, setDuaType] = useState(DUA_TYPES[0]);
  const [count, setCount] = useState(100);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!Number.isInteger(Number(count)) || Number(count) < 1 || Number(count) > 1000000) {
      setFeedback({ error: true, text: "Adet 1 ile 1.000.000 arasında bir tam sayı olmalıdır." });
      return;
    }
    setFeedback(null);
    setBusy(true);
    try {
      await api.post("/dua", { dua_type: duaType, count: Number(count), note: note || null });
      toast.success(translate("Dua bağışınız alındı"), {
        description: translate("Kaydınız hesabınıza eklendi. Allah kabul etsin."),
      });
      setFeedback({ error: false, text: "Dua bağışınız kaydedildi." });
      setNote("");
      setCount(100);
    } catch (err) {
      const text = formErrorMessage(err, "Bağış gönderilemedi. Bilgileriniz korunuyor; tekrar deneyebilirsiniz.");
      setFeedback({ error: true, text });
      toast.error(translate(text));
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = { background: "rgba(255,255,255,0.04)", borderColor: "var(--sy-border)", color: "var(--sy-text)" };

  if (loading) return null;

  return (
    <div className="px-6 py-10 lg:px-10">
      <div className="mx-auto max-w-2xl sy-fade-in">
        <div className="mb-7 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border" style={{ borderColor: "rgba(201,167,101,0.35)", background: "rgba(201,167,101,0.1)" }}>
            <HeartHandshake size={22} style={{ color: "var(--sy-gold)" }} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-semibold sy-gold-text">{translate("Dua Bağışı")}</h1>
            <p className="text-sm" style={{ color: "var(--sy-text-dim)" }}>{" "}{translate("Hatim, Salâtü Selâm, Kelime-i Tevhid, Sure ve Tesbihat bağışlayın.")}{" "}</p>
          </div>
        </div>

        {!user ? (
          <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)" }}>
            <p className="mb-5" style={{ color: "var(--sy-text)" }}>{" "}{translate("Dua bağışınızı hesabınıza kaydetmek için lütfen giriş yapın.")}{" "}</p>
            <Link to="/giris">
              <Button className="gap-2 font-semibold" style={{ background: "linear-gradient(180deg,#e8d5a8 0%,#d6b876 55%,#b8954f 100%)", color: "#1a150b" }}>
                <LogIn size={16} />{" "}{translate("Giriş Yap")}{" "}</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} aria-busy={busy} className="rounded-2xl border p-7 space-y-5" style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)" }}>
            <div>
              <label htmlFor="dua-type" className="mb-2 block text-sm font-medium" style={{ color: "var(--sy-gold-bright)" }}>{translate("Bağış Türü")}</label>
              <Select value={duaType} onValueChange={setDuaType}>
                <SelectTrigger id="dua-type" style={fieldStyle}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DUA_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="dua-count" className="mb-2 block text-sm font-medium" style={{ color: "var(--sy-gold-bright)" }}>{translate("Adet")}</label>
              <div className="flex items-center gap-2">
                <Button type="button" aria-label={translate("Adedi 10 azalt")} onClick={() => setCount((c) => Math.max(1, Number(c) - 10))} className="shrink-0 border" style={{ background: "transparent", color: "var(--sy-gold)", borderColor: "var(--sy-border)" }}>
                  <Minus size={16} />
                </Button>
                <Input id="dua-count" name="count" type="number" min={1} max={1000000} step={1} required value={count} onChange={(e) => setCount(e.target.value)} className="min-w-0 text-center" style={fieldStyle} />
                <Button type="button" aria-label={translate("Adedi 10 artır")} onClick={() => setCount((c) => Math.min(1000000, Number(c) + 10))} className="shrink-0 border" style={{ background: "transparent", color: "var(--sy-gold)", borderColor: "var(--sy-border)" }}>
                  <Plus size={16} />
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {[33, 100, 500, 1000].map((n) => (
                  <button key={n} type="button" onClick={() => setCount(n)} className="rounded-lg border px-3 py-1 text-xs transition-colors hover:bg-[color:var(--sy-panel-2)]" style={{ borderColor: "var(--sy-border)", color: "var(--sy-text-dim)" }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="dua-note" className="mb-2 block text-sm font-medium" style={{ color: "var(--sy-gold-bright)" }}>{translate("Not (isteğe bağlı)")}</label>
              <Textarea id="dua-note" name="note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder={translate("Niyetinizi veya bir notu yazabilirsiniz…")} style={fieldStyle} />
            </div>

            {feedback && <p role={feedback.error ? "alert" : "status"}>{translate(feedback.text)}</p>}
            <Button type="submit" disabled={busy} className="w-full gap-2 font-semibold" style={{ background: "linear-gradient(180deg,#e8d5a8 0%,#d6b876 55%,#b8954f 100%)", color: "#1a150b" }}>
              {busy && <Loader2 size={16} className="animate-spin" />} <HeartHandshake size={16} />{" "}{translate("Dua Gönder")}{" "}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
